import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Process } from '@/types/database';
import { useAuth } from '@/hooks/use-auth';
import { ArrowLeft, Save, Upload, FileText, CheckSquare, Link as LinkIcon, File, X, Undo } from 'lucide-react';
import { toast as sonnerToast } from "sonner";
import { useFileUpload } from '@/pages/workflow/hooks/useFileUpload';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import { MarkdownRenderer } from '@/components/MarkdownRenderer';
import { ChecklistDialog } from '@/components/ChecklistDialog';

async function fetchProcess(id: string) {
  const { data, error } = await supabase
    .from('processes')
    .select('*, process_attachments(*)')
    .eq('id', id)
    .single();

  if (error) {
    if(error.code !== 'PGRST116'){ // Ignore "No rows found" error
        console.error('Error fetching process:', error);
        throw new Error(error.message);
    }
  }
  return data;
}

const ProcessEditorPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { uploadFile, isUploading } = useFileUpload('process-attachments');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [cursorPosition, setCursorPosition] = useState<number>(0);
  const [lastSavedContent, setLastSavedContent] = useState('');
  
  // Estados para histórico de desfazer
  const [contentHistory, setContentHistory] = useState<string[]>([]);
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState(-1);

  const isNew = id === 'new';

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [isEditMode, setIsEditMode] = useState(true);

  const { data: processData, isLoading } = useQuery({
    queryKey: ['process', id],
    queryFn: () => fetchProcess(id!),
    enabled: !isNew,
  });

  useEffect(() => {
    if (processData) {
      setTitle(processData.title);
      setContent(processData.content || '');
      setLastSavedContent(processData.content || '');
      // Inicializar histórico com o conteúdo carregado
      setContentHistory([processData.content || '']);
      setCurrentHistoryIndex(0);
    }
  }, [processData]);

  // Função para adicionar ao histórico
  const addToHistory = useCallback((newContent: string) => {
    setContentHistory(prev => {
      // Remove itens após o índice atual (quando estamos no meio do histórico)
      const newHistory = prev.slice(0, currentHistoryIndex + 1);
      // Adiciona o novo conteúdo
      newHistory.push(newContent);
      // Limita o histórico a 50 itens
      if (newHistory.length > 50) {
        newHistory.shift();
        setCurrentHistoryIndex(prev => Math.max(0, prev));
        return newHistory;
      }
      setCurrentHistoryIndex(newHistory.length - 1);
      return newHistory;
    });
  }, [currentHistoryIndex]);

  // Função para desfazer
  const handleUndo = useCallback(() => {
    if (currentHistoryIndex > 0) {
      const newIndex = currentHistoryIndex - 1;
      setCurrentHistoryIndex(newIndex);
      const previousContent = contentHistory[newIndex];
      setContent(previousContent);
      
      // Manter foco no textarea
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    }
  }, [currentHistoryIndex, contentHistory]);

  // Função para capturar a posição do cursor
  const updateCursorPosition = useCallback(() => {
    if (textareaRef.current) {
      setCursorPosition(textareaRef.current.selectionStart);
    }
  }, []);

  // Função para inserir texto na posição exata do cursor
  const insertTextAtCursor = useCallback((textToInsert: string) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const beforeCursor = content.substring(0, start);
    const afterCursor = content.substring(end);
    const newContent = beforeCursor + textToInsert + afterCursor;
    
    setContent(newContent);
    // Adicionar ao histórico
    addToHistory(newContent);
    
    // Aguardar o React atualizar o estado e então posicionar o cursor
    setTimeout(() => {
      if (textarea) {
        const newCursorPosition = start + textToInsert.length;
        textarea.focus();
        textarea.setSelectionRange(newCursorPosition, newCursorPosition);
        setCursorPosition(newCursorPosition);
      }
    }, 10);
  }, [content, addToHistory]);

  // Função para download de arquivo
  const downloadAttachment = useCallback((attachment: any) => {
    const link = document.createElement('a');
    link.href = attachment.url;
    link.download = attachment.name;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, []);

  // Função para processar cliques em menções
  const handleContentClick = useCallback((event: React.MouseEvent<HTMLTextAreaElement>) => {
    const textarea = event.currentTarget;
    const clickPosition = textarea.selectionStart;
    const text = textarea.value;
    
    // Procurar por menções próximas à posição do clique
    const beforeClick = text.substring(0, clickPosition);
    const afterClick = text.substring(clickPosition);
    
    // Regex para encontrar menções (@nomedoarquivo)
    const mentionRegex = /@(\w+)/g;
    let match;
    
    while ((match = mentionRegex.exec(text)) !== null) {
      const mentionStart = match.index;
      const mentionEnd = mentionStart + match[0].length;
      
      // Verificar se o clique foi dentro da menção
      if (clickPosition >= mentionStart && clickPosition <= mentionEnd) {
        const mentionName = match[1];
        
        // Encontrar o anexo correspondente
        const attachment = processData?.process_attachments?.find(att => 
          att.name.replace(/\.[^/.]+$/, "").replace(/[^a-zA-Z0-9]/g, "") === mentionName
        );
        
        if (attachment) {
          downloadAttachment(attachment);
          sonnerToast.success(`Baixando ${attachment.name}...`);
        }
        break;
      }
    }
  }, [processData, downloadAttachment]);

  // Salvamento automático
  const autoSaveMutation = useMutation({
    mutationFn: async ({ title, content }: { title: string; content?: string }) => {
      if (!user || isNew) return null;

      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('company_id')
        .eq('id', user.id)
        .single();
    
      if (userError) {
        console.error('Error fetching user data:', userError);
        return null;
      }
      
      if (!userData?.company_id) return null;

      const processToSave = {
        title,
        content: content || '',
        user_id: user.id,
        company_id: userData.company_id,
      };

      const { data, error } = await supabase
        .from('processes')
        .update(processToSave)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      setLastSavedContent(content);
      queryClient.invalidateQueries({ queryKey: ['process', id] });
    },
    onError: (error: Error) => {
      console.error('Auto-save error:', error);
    },
  });

  // Auto-save a cada 2 segundos quando há mudanças
  useEffect(() => {
    if (!isNew && title && content !== lastSavedContent && content.trim() !== '') {
      const timeoutId = setTimeout(() => {
        autoSaveMutation.mutate({ title, content });
      }, 2000);

      return () => clearTimeout(timeoutId);
    }
  }, [content, title, lastSavedContent, isNew, autoSaveMutation]);
  
  const upsertProcessMutation = useMutation({
    mutationFn: async ({ title, content }: { title: string; content?: string }) => {
      if (!user) throw new Error("Usuário não autenticado.");

      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('company_id')
        .eq('id', user.id)
        .single();
    
      if (userError) {
        console.error('Error fetching user data:', userError);
        throw new Error(`Erro ao buscar dados do usuário: ${userError.message}`);
      }
      
      if (!userData?.company_id) throw new Error("Empresa não encontrada para o usuário.");

      const processToSave = {
        title,
        content: content || '',
        user_id: user.id,
        company_id: userData.company_id,
      };

      let result;
      if (isNew) {
        const { data, error } = await supabase
          .from('processes')
          .insert(processToSave)
          .select()
          .single();
        
        if (error) throw error;
        result = data;
      } else {
        const { data, error } = await supabase
          .from('processes')
          .update(processToSave)
          .eq('id', id)
          .select()
          .single();
        
        if (error) throw error;
        result = data;
      }

      return result;
    },
    onSuccess: (data) => {
      sonnerToast.success("Processo salvo com sucesso.");
      setLastSavedContent(content);
      queryClient.invalidateQueries({ queryKey: ['processes'] });
      queryClient.invalidateQueries({ queryKey: ['process', data.id] });
      if(isNew) {
        navigate(`/processes/${data.id}`, { replace: true });
      }
    },
    onError: (error: Error) => {
      console.error('Save error:', error);
      sonnerToast.error(`Não foi possível salvar o processo: ${error.message}`);
    },
  });

  const handleFileUpload = async (file: File) => {
    if (!id || id === 'new') {
      sonnerToast.error("Salve o processo primeiro antes de adicionar anexos.");
      return;
    }

    // Salvar antes de anexar o arquivo para garantir que o conteúdo não seja perdido
    if (content !== lastSavedContent) {
      try {
        await autoSaveMutation.mutateAsync({ title, content });
      } catch (error) {
        console.error('Erro ao salvar antes do upload:', error);
      }
    }

    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}.${fileExt}`;
      const filePath = `process_${id}/${fileName}`;
      
      // Upload direto para o Supabase Storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('process-attachments')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        console.error('Upload error:', uploadError);
        throw new Error(`Erro no upload: ${uploadError.message}`);
      }

      // Obter URL pública
      const { data: { publicUrl } } = supabase.storage
        .from('process-attachments')
        .getPublicUrl(filePath);

      // Salvar referência do arquivo no banco
      const { error: dbError } = await supabase
        .from('process_attachments')
        .insert({
          process_id: id,
          name: file.name,
          url: publicUrl,
          path: filePath,
          file_type: file.type,
          size_bytes: file.size,
          user_id: user!.id
        });

      if (dbError) {
        console.error('Database error:', dbError);
        throw new Error(`Erro ao salvar no banco: ${dbError.message}`);
      }

      sonnerToast.success("Arquivo anexado com sucesso!");
      queryClient.invalidateQueries({ queryKey: ['process', id] });
      
      // Gerar um nome de menção baseado no nome do arquivo
      const documentMention = ` @${file.name.replace(/\.[^/.]+$/, "").replace(/[^a-zA-Z0-9]/g, "")} `;
      
      // Inserir menção do documento na posição atual do cursor
      insertTextAtCursor(documentMention);
    } catch (error) {
      console.error('Error uploading file:', error);
      sonnerToast.error("Erro ao anexar arquivo.");
    }
  };

  const handleSave = () => {
    if (!title) {
        sonnerToast.error("O título é obrigatório.");
        return;
    }
    
    upsertProcessMutation.mutate({
      title,
      content
    });
  };

  const addDocument = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf,.doc,.docx,.txt,.jpg,.jpeg,.png';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        handleFileUpload(file);
      }
    };
    input.click();
  };

  const addChecklist = (checklistItems: string[]) => {
    checklistItems.forEach(item => {
      insertTextAtCursor(item);
    });
  };

  const addLink = () => {
    const linkTemplate = `\n## 🔗 Link Útil\n\n[Texto do link](https://exemplo.com)\n`;
    insertTextAtCursor(linkTemplate);
  };

  const deleteAttachmentMutation = useMutation({
    mutationFn: async (attachmentId: string) => {
      const { error } = await supabase
        .from('process_attachments')
        .delete()
        .eq('id', attachmentId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      sonnerToast.success("Anexo excluído com sucesso!");
      queryClient.invalidateQueries({ queryKey: ['process', id] });
    },
    onError: (error: Error) => {
      console.error('Delete error:', error);
      sonnerToast.error(`Erro ao excluir anexo: ${error.message}`);
    },
  });

  const handleDeleteAttachment = async (attachmentId: string) => {
    if (window.confirm('Tem certeza que deseja excluir este anexo?')) {
      deleteAttachmentMutation.mutate(attachmentId);
    }
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newContent = e.target.value;
    setContent(newContent);
    updateCursorPosition();
    
    // Adicionar ao histórico apenas se houve mudança significativa (mais de 10 caracteres de diferença)
    const currentContent = contentHistory[currentHistoryIndex] || '';
    if (Math.abs(newContent.length - currentContent.length) > 10) {
      addToHistory(newContent);
    }
  };

  if (isLoading && !isNew) return <p>Carregando processo...</p>;

  const canUndo = currentHistoryIndex > 0;

  return (
    <div className="container mx-auto p-4 md:p-8">
        <div className="flex justify-between items-center mb-6">
            <Button variant="ghost" onClick={() => navigate('/processes')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Voltar
            </Button>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={handleUndo}
                disabled={!canUndo}
                title="Desfazer (Ctrl+Z)"
              >
                <Undo className="mr-2 h-4 w-4" />
                Desfazer
              </Button>
              <Button 
                variant="outline" 
                onClick={() => setIsEditMode(!isEditMode)}
              >
                {isEditMode ? 'Visualizar' : 'Editar'}
              </Button>
              <Button onClick={handleSave} disabled={upsertProcessMutation.isPending}>
                <Save className="mr-2 h-4 w-4" />
                {upsertProcessMutation.isPending ? 'Salvando...' : 'Salvar'}
              </Button>
            </div>
        </div>
        
        {/* Indicador de salvamento automático */}
        <div className="mb-4">
          {content !== lastSavedContent && !isNew && (
            <p className="text-sm text-yellow-600">
              📝 Salvamento automático em andamento...
            </p>
          )}
          {content === lastSavedContent && !isNew && (
            <p className="text-sm text-green-600">
              ✅ Salvo automaticamente
            </p>
          )}
        </div>
        
        <div className="space-y-6">
            <Input
            placeholder="Título do Processo"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="text-3xl font-bold h-auto p-2 border-0 focus-visible:ring-0 shadow-none"
            />
            
            {isEditMode ? (
              <ContextMenu>
                <ContextMenuTrigger asChild>
                  <div className="relative">
                    <Textarea
                      ref={textareaRef}
                      placeholder="Descreva o processo aqui. Use markdown para formatação, como # Título, - para listas e **para negrito**. Clique com o botão direito para adicionar elementos. Clique em @menções para baixar anexos."
                      value={content || ''}
                      onChange={handleTextareaChange}
                      onClick={handleContentClick}
                      onKeyUp={updateCursorPosition}
                      onSelect={updateCursorPosition}
                      onFocus={updateCursorPosition}
                      onKeyDown={(e) => {
                        // Atalho Ctrl+Z para desfazer
                        if (e.ctrlKey && e.key === 'z' && !e.shiftKey) {
                          e.preventDefault();
                          handleUndo();
                        }
                      }}
                      className="min-h-[50vh] text-base border-0 focus-visible:ring-0 shadow-none cursor-text"
                      style={{ 
                        fontFamily: 'inherit',
                        resize: 'vertical'
                      }}
                    />
                  </div>
                </ContextMenuTrigger>
                <ContextMenuContent className="w-52">
                  <ContextMenuItem onClick={addDocument} className="cursor-pointer" disabled={isUploading}>
                    <Upload className="mr-2 h-4 w-4" />
                    {isUploading ? 'Enviando...' : 'Anexar Documento'}
                  </ContextMenuItem>
                  <ChecklistDialog onAddChecklist={addChecklist}>
                    <ContextMenuItem className="cursor-pointer" onSelect={(e) => e.preventDefault()}>
                      <CheckSquare className="mr-2 h-4 w-4" />
                      Criar Checklist
                    </ContextMenuItem>
                  </ChecklistDialog>
                  <ContextMenuItem onClick={addLink} className="cursor-pointer">
                    <LinkIcon className="mr-2 h-4 w-4" />
                    Adicionar Link
                  </ContextMenuItem>
                </ContextMenuContent>
              </ContextMenu>
            ) : (
              <div className="min-h-[50vh] p-4 border rounded-md">
                <MarkdownRenderer 
                  content={content} 
                  onContentChange={setContent}
                  readonly={false}
                />
              </div>
            )}
        </div>
        
        {/* Seção de anexos na parte inferior - apenas para visualização e gerenciamento */}
        {!isNew && processData?.process_attachments && processData.process_attachments.length > 0 && (
            <div className="mt-8">
                <h2 className="text-2xl font-semibold mb-4">Anexos do Processo</h2>
                <div className="p-4 border rounded-md bg-background">
                  <div className="space-y-2">
                    {processData.process_attachments.map((attachment) => (
                      <div key={attachment.id} className="flex items-center justify-between p-2 border rounded">
                        <div className="flex items-center space-x-2 flex-1">
                          <FileText className="h-4 w-4" />
                          <button 
                            onClick={() => downloadAttachment(attachment)}
                            className="text-blue-600 hover:underline flex-1 text-left"
                          >
                            {attachment.name}
                          </button>
                          <span className="text-xs text-gray-500">
                            {attachment.size_bytes ? (attachment.size_bytes / 1024 / 1024).toFixed(2) + ' MB' : ''}
                          </span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteAttachment(attachment.id)}
                          disabled={deleteAttachmentMutation.isPending}
                          className="text-red-600 hover:text-red-800 hover:bg-red-50"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                <Button 
                  variant="outline" 
                  className="mt-4" 
                  onClick={addDocument}
                  disabled={isUploading}
                >
                    <Upload className="mr-2 h-4 w-4" />
                    {isUploading ? 'Enviando...' : 'Adicionar Anexo'}
                </Button>
                </div>
            </div>
        )}
    </div>
  );
};

export default ProcessEditorPage;
